import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Clock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { EASE } from "@/lib/motion";
import { Seo } from "@/components/seo/Seo";
import { SEO } from "@/data/seo";

const COPY = {
  checking: { title: "Stiamo confermando il pagamento…", text: "Ci vorrà solo qualche secondo." },
  paid: { title: "Grazie. Il tuo ordine è confermato.", text: "Abbiamo ricevuto il pagamento e ti abbiamo inviato un'email con il riepilogo dell'ordine. Ti scriveremo di nuovo quando il pacco partirà." },
  expired: { title: "La sessione di pagamento è scaduta.", text: "Il tuo carrello è ancora disponibile: puoi riprovare quando vuoi." },
  timeout: { title: "Non riusciamo ancora a confermare il pagamento.", text: "Se hai completato il pagamento, riceverai comunque la conferma. In caso di dubbi contatta l'assistenza." },
  error: { title: "Sessione di pagamento non trovata.", text: "Torna al negozio per continuare i tuoi acquisti." },
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState(sessionId ? "checking" : "error");

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data?.payment_status === "paid") {
          setState("paid");
          clear();
          return;
        }
        if (data?.payment_status === "expired") {
          setState("expired");
          return;
        }
      } catch {
        if (cancelled) return;
      }
      if (attempts >= 10) setState("timeout");
      else timer = setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId, clear]);

  const copy = COPY[state];
  return (
    <div className="grain relative flex min-h-screen items-center bg-ivory pb-24 pt-40" data-testid="payment-success-page">
      <Seo {...SEO.private} />
      <div className="container-pf relative z-10 max-w-3xl text-center">
        <motion.span initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.9, ease: EASE }} className="mx-auto grid h-24 w-24 place-items-center rounded-full border border-copper/40 text-copper">
          {state === "paid" ? <Check className="h-10 w-10" strokeWidth={1.4} /> : <Clock className={`h-10 w-10 ${state === "checking" ? "animate-pulse" : ""}`} strokeWidth={1.4} />}
        </motion.span>
        <h1 className="mt-10 font-display text-4xl font-medium leading-tight text-navy sm:text-6xl" data-testid="payment-status-title">{copy.title}</h1>
        <p className="mx-auto mt-6 max-w-xl text-lg text-slate2">{copy.text}</p>
        {state === "paid" && sessionId && <p className="mt-6 text-[15px] text-slate2" data-testid="order-reference">Riferimento ordine: <strong className="text-navy">{sessionId.slice(-8).toUpperCase()}</strong></p>}
        {state !== "checking" && (
          <Link to="/collezione" className="btn-primary mt-12" data-testid="payment-back-to-shop">
            Torna alla collezione
          </Link>
        )}
      </div>
    </div>
  );
}
